import axios from '../axios';
import {
  ADD_PRODUCT,
  GET_PRODUCTS_BY_MANAGING_BRANCH,
  GET_PRODUCT_CATEGORY,
  REGIONAL_UPDATE_PRODUCT,
  REGIONAL_UPDATE_PRODUCT_STATUS,
  UPLOAD_IMAGE_CLOUDINARY,
} from '../ApiUrl';

/**
 * Maps one product from API `data[]` to table row shape.
 */
export function mapProductApiToRow(item, index) {
  const id = String(item._id ?? item.id ?? item.product_id ?? `p-${index}`);
  const name =
    item.product_name ?? item.name ?? item.title ?? item.item_name ?? '—';
  const kitchen =
    item.kitchen_name ?? item.kitchenName ?? item.kitchen ?? '—';
  const priceRaw = item.price ?? item.product_price ?? item.amount ?? item.unit_price;
  const price =
    priceRaw != null && priceRaw !== ''
      ? typeof priceRaw === 'number'
        ? priceRaw
        : Number.parseFloat(String(priceRaw).replace(/[^0-9.-]/g, '')) || null
      : null;
  const category =
    item.category ?? item.product_category ?? item.category_name ?? item.type ?? '—';
  const status =
    item.status != null
      ? String(item.status)
      : item.is_active === false
        ? 'Inactive'
        : item.is_active === true
          ? 'Active'
          : '—';
  return {
    id,
    name: String(name),
    kitchen: String(kitchen),
    price,
    category: String(category),
    status,
  };
}

/**
 * Normalizes API item to the shape used by the kitchen-style product list (filters, table, stats).
 */
export function normalizeProductForKitchenList(item, index) {
  const row = mapProductApiToRow(item, index);
  const sku = item.product_sku ?? item.sku ?? '';
  const qtyRaw = item.available_quantity ?? item.quantity ?? item.stock ?? item.inventory_count;
  const qtyNum =
    qtyRaw != null && qtyRaw !== '' && !Number.isNaN(Number(qtyRaw)) ? Number(qtyRaw) : 0;

  const rawStatus = item.status_type ?? item.status ?? item.workflow_status;
  let status_type = '';
  if (typeof rawStatus === 'string' && rawStatus.trim()) {
    status_type = rawStatus.trim().toLowerCase().replace(/\s+/g, '_');
  } else if (rawStatus === true) {
    status_type = 'approved';
  } else if (item.is_active === true && !status_type) {
    status_type = 'active';
  } else if (item.is_active === false && !status_type) {
    status_type = 'inactive';
  } else if (!status_type) {
    status_type = 'unknown';
  }

  const product_type = String(
    item.product_type ?? item.menu_type ?? item.item_type ?? 'non_meal_item'
  );
  const cat = row.category === '—' ? '' : String(row.category);

  // Keep full API payload on each row so preview/edit drawers receive description, cuisine,
  // serving_days, toppings, etc. (previously only a thin subset was passed through).
  return {
    ...item,
    ...row,
    product_id: item._id ?? item.id ?? row.id,
    product_name: row.name,
    product_category: cat,
    product_type,
    status_type,
    available_quantity: qtyNum,
    selling_price: row.price,
    price: row.price != null ? row.price : item.price,
    product_sku: String(sku),
    product_promotion:
      item.product_promotion && typeof item.product_promotion === 'object'
        ? item.product_promotion
        : {},
    image_url: item.image_url,
  };
}

const MIN_KITCHEN_NAME_LEN = 4;

/**
 * Maps internal list filter values (e.g. meal_item) to API enums (e.g. Meal_Item).
 */
export function mapProductTypeFilterToApi(internalValue) {
  if (internalValue == null || internalValue === '' || internalValue === 'None') {
    return undefined;
  }
  const s = String(internalValue).trim();
  if (s === 'meal_item') return 'Meal_Item';
  if (s === 'non_meal_item') return 'Non_Meal_Item';
  return s
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('_');
}

/**
 * GetProductsByManagingBranch — allowed `status_type` filter values (lowercase snake_case).
 */
export const PRODUCTS_LIST_STATUS_TYPE_VALUES = [
  'approved',
  'admin_review_pending',
  'admin_rejected',
  'merchant_approved',
  'merchant_rejected',
  'ai_generated_merchant_review_pending',
  'merchant_review_pending',
];

const PRODUCTS_LIST_STATUS_TYPE_SET = new Set(PRODUCTS_LIST_STATUS_TYPE_VALUES);

/**
 * UI filter values that are not API enums but map to the closest allowed `status_type`.
 */
const UI_STATUS_FILTER_TO_API = {
  merchant_image_review_pending: 'merchant_review_pending',
  merchant_content_review_pending: 'merchant_review_pending',
  content_rejected_by_admin: 'admin_rejected',
  image_rejected_by_admin: 'admin_rejected',
  content_rejected_by_merchant: 'merchant_rejected',
  image_rejected_by_merchant: 'merchant_rejected',
  content_approved_merchant: 'merchant_approved',
};

/**
 * Maps internal status filter to API `status_type` (must be one of PRODUCTS_LIST_STATUS_TYPE_VALUES).
 */
export function mapStatusTypeFilterToApi(internalValue) {
  if (internalValue == null || internalValue === '' || internalValue === 'all') {
    return undefined;
  }
  const key = String(internalValue)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (PRODUCTS_LIST_STATUS_TYPE_SET.has(key)) {
    return key;
  }
  const mapped = UI_STATUS_FILTER_TO_API[key];
  if (mapped && PRODUCTS_LIST_STATUS_TYPE_SET.has(mapped)) {
    return mapped;
  }
  return undefined;
}

/**
 * POST /GetProductsByManagingBranch
 * Body: managing_branch_name (required); optional kitchen_name (min 4 chars when sent);
 * product_type, product_category, status_type, product_name_search; page, limit.
 */
export const getProductsByManagingBranchAPI = async ({
  managing_branch_name,
  kitchen_name,
  page = 1,
  limit = 20,
  product_type,
  product_category,
  status_type,
  product_name_search,
}) => {
  try {
    const branch = String(managing_branch_name ?? '')
      .trim()
      .toLowerCase()
      .slice(0, 50);
    if (!branch) {
      return { hasError: true, message: 'Managing branch name is required.' };
    }

    const body = {
      managing_branch_name: branch,
      page: Math.max(1, parseInt(String(page), 10) || 1),
    };
    let safeLimit = parseInt(String(limit), 10) || 20;
    if (safeLimit < 1) safeLimit = 1;
    if (safeLimit > 100) safeLimit = 100;
    body.limit = safeLimit;

    const kn = kitchen_name != null ? String(kitchen_name).trim().toLowerCase() : '';
    if (kn.length >= MIN_KITCHEN_NAME_LEN) {
      body.kitchen_name = kn;
    }

    const pt = mapProductTypeFilterToApi(product_type);
    if (pt) {
      body.product_type = pt;
    }

    const cat = product_category != null ? String(product_category).trim() : '';
    if (cat !== '') {
      body.product_category = cat;
    }

    const stApi = mapStatusTypeFilterToApi(status_type);
    if (stApi) {
      body.status_type = stApi;
    }

    const search = product_name_search != null ? String(product_name_search).trim() : '';
    if (search !== '') {
      body.product_name_search = search;
    }

    const response = await axios.post(GET_PRODUCTS_BY_MANAGING_BRANCH, body);
    return response;
  } catch (error) {
    console.log('getProductsByManagingBranchAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to load products.' };
  }
};

/**
 * Normalizes `data` from GET /GetProductCategory (grouped string arrays).
 */
export function normalizeProductCategoryData(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      RegularMenuCategory: [],
      ComboMenuCategory: [],
      PromotionalStoreCategory: [],
      CloudKitchenComboMenuCategory: [],
    };
  }
  const pick = (key) => (Array.isArray(raw[key]) ? raw[key] : []);
  return {
    RegularMenuCategory: pick('RegularMenuCategory'),
    ComboMenuCategory: pick('ComboMenuCategory'),
    PromotionalStoreCategory: pick('PromotionalStoreCategory'),
    CloudKitchenComboMenuCategory: pick('CloudKitchenComboMenuCategory'),
  };
}

/**
 * @returns {{ ok: true, data: object, message?: string } | { ok: false, message: string, data: null }}
 */
export function parseProductCategoryAPIResponse(res) {
  if (res?.hasError) {
    return { ok: false, message: res.message || 'Request failed', data: null };
  }
  const payload = res?.data ?? {};
  if (payload.success !== true) {
    return {
      ok: false,
      message: payload.message || 'Failed to load categories',
      data: null,
    };
  }
  return {
    ok: true,
    message: payload.message || '',
    data: normalizeProductCategoryData(payload.data),
  };
}

/** GET /GetProductCategory */
export const getProductCategoryAPI = async () => {
  try {
    return await axios.get(GET_PRODUCT_CATEGORY);
  } catch (error) {
    console.log('getProductCategoryAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to load categories.' };
  }
};

function uniqueSortedStrings(values) {
  return [
    ...new Set(
      (values || []).filter((v) => v != null && String(v).trim() !== '').map((v) => String(v))
    ),
  ].sort((a, b) => a.localeCompare(b));
}

/**
 * Options for the regional product list category filter (aligned with Kitchen menu groupings).
 */
export function getCategoryOptionsForListFilter(apiData, selectedProductType) {
  const d = apiData || normalizeProductCategoryData(null);
  const { RegularMenuCategory: R, ComboMenuCategory: C, PromotionalStoreCategory: P, CloudKitchenComboMenuCategory: CK } = d;

  if (selectedProductType === 'None') {
    return uniqueSortedStrings([...R, ...C, ...P, ...CK]);
  }
  if (selectedProductType === 'non_meal_item') {
    return uniqueSortedStrings([...R, ...P, ...CK]);
  }
  if (selectedProductType === 'meal_item') {
    return uniqueSortedStrings([...C, ...P, ...CK]);
  }
  return uniqueSortedStrings([...R, ...C, ...P, ...CK]);
}

/**
 * POST /AddProduct — regional branch product create (includes managing_branch_name).
 */
export const addProductAPI = async (body) => {
  try {
    return await axios.post(ADD_PRODUCT, body);
  } catch (error) {
    console.log('addProductAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to add product.' };
  }
};

/** POST multipart/form-data — same contract as Kitchen `UploadImageCloudinaryAPI`. */
export const uploadImageCloudinaryAPI = async (formData) => {
  try {
    return await axios.post(UPLOAD_IMAGE_CLOUDINARY, formData);
  } catch (error) {
    console.log('uploadImageCloudinaryAPI catch', error);
    return { hasError: true, message: error?.message || 'Image upload failed.', data: { success: false } };
  }
};

/**
 * PUT /RegionalUpdateProduct — regional branch update (price, product_promotion, ingredients forbidden).
 * @param {Record<string, unknown>} body
 */
export const regionalUpdateProductAPI = async (body) => {
  try {
    return await axios.put(REGIONAL_UPDATE_PRODUCT, body);
  } catch (error) {
    console.log('regionalUpdateProductAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to update product.' };
  }
};

/**
 * PUT /RegionalUpdateProductStatus — regional status / review fields (kitchen_name + product_sku required).
 * @param {Record<string, unknown>} body
 */
export const regionalUpdateProductStatusAPI = async (body) => {
  try {
    return await axios.put(REGIONAL_UPDATE_PRODUCT_STATUS, body);
  } catch (error) {
    console.log('regionalUpdateProductStatusAPI catch', error);
    return { hasError: true, message: error?.message || 'Failed to update product status.' };
  }
};

const REGIONAL_FORBIDDEN_BODY_KEYS = ['price', 'product_promotion', 'ingredients'];

/**
 * Returns a shallow copy safe to send to /RegionalUpdateProduct (forbidden keys removed).
 * @param {Record<string, unknown>} payload
 */
export function omitRegionalForbiddenProductKeys(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const next = { ...payload };
  for (const k of REGIONAL_FORBIDDEN_BODY_KEYS) {
    if (Object.prototype.hasOwnProperty.call(next, k)) {
      delete next[k];
    }
  }
  return next;
}

/**
 * Maps API validation / failure payloads to ErrorForm `{ errorMessage, param }[]`.
 * @param {object} body — `res.data` from regional update
 */
export function mapRegionalProductUpdateErrorsToForm(body) {
  if (!body || typeof body !== 'object') {
    return [{ errorMessage: 'Update failed.', param: 'update_product' }];
  }
  const rawList = body.errors;
  if (Array.isArray(rawList) && rawList.length > 0) {
    return rawList.map((e) => ({
      errorMessage:
        e?.errorMessage ?? e?.message ?? e?.msg ?? e?.CustomMessage ?? String(e?.error ?? 'Validation error'),
      param: e?.param ?? e?.field ?? 'field',
    }));
  }
  if (body.success === false) {
    const msg =
      body.CustomMessage ??
      body.message ??
      body.errorMessage ??
      (typeof body.error === 'string' ? body.error : null);
    if (msg) {
      return [{ errorMessage: msg, param: body.param ?? body.field ?? 'update_product' }];
    }
  }
  return [{ errorMessage: 'Something went wrong while updating the product.', param: 'update_product' }];
}
